import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  getAgelessMarketingContext,
  getGetAgelessMarketingContextQueryKey,
} from "@/api/orval/ageless-marketing-setup-link/ageless-marketing-setup-link";
import { useSafeAdminContext } from "@/providers";

import { platformDomainSlugSchema } from "../AgelessMarketingContainer/agelessMarketing.schemas";
import { resolvePlatformDomainSlug } from "../AgelessMarketingContainer/agelessMarketing.utils";

export type AgelessMarketingLocation = {
  readonly id: string;
  readonly name: string;
};

export type AgelessMarketingPlatform = {
  readonly name: string | null;
  readonly logoImageUrl: string | null;
  /** Subdomain every shareable Ageless link is built from; null when not configured. */
  readonly subdomainSlug: string | null;
  readonly brandColor: string | null;
  readonly locations: readonly AgelessMarketingLocation[];
  readonly isLoading: boolean;
};

const EMPTY: AgelessMarketingPlatform = {
  name: null,
  logoImageUrl: null,
  subdomainSlug: null,
  brandColor: null,
  locations: [],
  isLoading: false,
};

/**
 * The platform facts the Ageless Marketing page renders, from whichever source the current
 * viewer has. A signed-in admin already has the full platform model on AdminContext; a
 * visitor on a shared setup link has no session, so the same facts come from the anonymous
 * marketing-context endpoint. Callers get one shape and never branch on who is looking.
 */
export function useAgelessMarketingPlatform(
  platformId: string | undefined,
): AgelessMarketingPlatform {
  const adminContext = useSafeAdminContext();
  const contextPlatform = adminContext?.platform;
  const hasSessionPlatform = !!platformId && contextPlatform?.id === platformId;

  const { data, isPending: isLoading } = useQuery({
    queryKey: getGetAgelessMarketingContextQueryKey(platformId ?? ""),
    queryFn: () => getAgelessMarketingContext(platformId ?? ""),
    enabled: !!platformId && !hasSessionPlatform,
    retry: false,
    staleTime: Infinity,
  });

  return useMemo(() => {
    if (!platformId) {
      return EMPTY;
    }

    if (hasSessionPlatform && contextPlatform) {
      return {
        name: contextPlatform.name,
        logoImageUrl: contextPlatform.logoImageUrl ?? null,
        subdomainSlug: resolvePlatformDomainSlug(contextPlatform),
        brandColor: contextPlatform.activePlatformTheme?.brandColor ?? null,
        locations: (contextPlatform.platformLocations ?? [])
          .filter((location) => location.active)
          .map((location) => ({ id: location.id, name: location.name ?? "" })),
        isLoading: false,
      };
    }

    if (!data) {
      return { ...EMPTY, isLoading };
    }

    const parsedSlug = platformDomainSlugSchema.safeParse(data.subdomainSlug ?? "");

    return {
      name: data.name,
      logoImageUrl: data.logoImageUrl ?? null,
      subdomainSlug: parsedSlug.success ? parsedSlug.data : null,
      brandColor: data.brandColor ?? null,
      locations: (data.locations ?? []).map((location) => ({
        id: location.locationId,
        name: location.name,
      })),
      isLoading: false,
    };
  }, [contextPlatform, data, hasSessionPlatform, isLoading, platformId]);
}
